import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Badge, Alert, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer, faPlus, faTrash, faCheckCircle, faTimesCircle, faInfoCircle, faSync } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';

/**
 * Server Registry Component
 * Manages VPS servers for redirector deployment
 */
const ServerRegistry = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    hostname: '',
    ipAddress: '',
    sshPort: 22,
    sshUsername: 'root',
    authMethod: 'password', // 'password' or 'privateKey'
    sshPassword: '',
    sshPrivateKey: '',
    notes: ''
  });

  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadServers();
  }, []);

  const loadServers = async () => {
    try {
      setLoading(true);
      // For now, store in localStorage (later: backend API)
      const storedServers = localStorage.getItem('c2_servers');
      if (storedServers) {
        setServers(JSON.parse(storedServers));
      }
    } catch (error) {
      console.error('Failed to load servers:', error);
      setError('Failed to load servers');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);

    try {
      const testConfig = {
        host: formData.ipAddress || formData.hostname,
        port: parseInt(formData.sshPort),
        username: formData.sshUsername,
        password: formData.authMethod === 'password' ? formData.sshPassword : undefined,
        privateKey: formData.authMethod === 'privateKey' ? formData.sshPrivateKey : undefined
      };

      const response = await c2Service.testSSHConnection(testConfig);

      if (response.data.success) {
        setTestResult({
          success: true,
          ...response.data.data
        });
        setSuccess('✅ Connection successful!');
      } else {
        setTestResult({
          success: false,
          message: response.data.data.message
        });
        setError(`❌ Connection failed: ${response.data.data.message}`);
      }

    } catch (error) {
      console.error('SSH test failed:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.error || error.message
      });
      setError(`❌ Test failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setTesting(false);
    }
  };

  const handleAddServer = async () => {
    try {
      // Validate
      if (!formData.name || !formData.hostname || !formData.ipAddress) {
        setError('Name, hostname, and IP address are required');
        return;
      }

      if (formData.authMethod === 'password' && !formData.sshPassword) {
        setError('SSH password is required');
        return;
      }

      if (formData.authMethod === 'privateKey' && !formData.sshPrivateKey) {
        setError('SSH private key is required');
        return;
      }

      // Test connection first
      if (!testResult?.success) {
        setError('Please test the connection before adding');
        return;
      }

      const newServer = {
        id: Date.now().toString(),
        ...formData,
        serverInfo: testResult.serverInfo,
        createdAt: new Date().toISOString(),
        status: 'online'
      };

      const updatedServers = [...servers, newServer];
      setServers(updatedServers);

      // Save to localStorage (later: backend API)
      localStorage.setItem('c2_servers', JSON.stringify(updatedServers));

      setSuccess(`✅ Server "${formData.name}" added successfully!`);
      setShowAddModal(false);
      resetForm();

    } catch (error) {
      console.error('Failed to add server:', error);
      setError('Failed to add server');
    }
  };

  const handleDeleteServer = (serverId) => {
    if (window.confirm('Are you sure you want to delete this server?')) {
      const updatedServers = servers.filter(s => s.id !== serverId);
      setServers(updatedServers);
      localStorage.setItem('c2_servers', JSON.stringify(updatedServers));
      setSuccess('Server deleted successfully');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      hostname: '',
      ipAddress: '',
      sshPort: 22,
      sshUsername: 'root',
      authMethod: 'password',
      sshPassword: '',
      sshPrivateKey: '',
      notes: ''
    });
    setTestResult(null);
    setError(null);
    setSuccess(null);
  };

  const renderTooltip = (text) => (
    <Tooltip id="tooltip">{text}</Tooltip>
  );

  return (
    <>
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#374151', borderColor: '#4b5563', color: '#fff' }}>
          <div>
            <FontAwesomeIcon icon={faServer} className="me-2" />
            Server Registry
            <OverlayTrigger placement="right" overlay={renderTooltip("Manage VPS servers for redirector deployment")}>
              <FontAwesomeIcon icon={faInfoCircle} className="ms-2" style={{ cursor: 'help', fontSize: '0.9rem' }} />
            </OverlayTrigger>
          </div>
          <Button variant="primary" size="sm" onClick={() => { resetForm(); setShowAddModal(true); }}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Server
          </Button>
        </Card.Header>
        <Card.Body style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : servers.length === 0 ? (
            <Alert variant="info">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              No servers registered. Add a VPS server to deploy redirectors.
            </Alert>
          ) : (
            <Table hover responsive variant="dark">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Hostname</th>
                  <th>IP Address</th>
                  <th>OS</th>
                  <th>SSH</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {servers.map(server => (
                  <tr key={server.id}>
                    <td>
                      <strong>{server.name}</strong>
                      {server.notes && (
                        <OverlayTrigger placement="right" overlay={renderTooltip(server.notes)}>
                          <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
                        </OverlayTrigger>
                      )}
                    </td>
                    <td><code>{server.hostname}</code></td>
                    <td><code>{server.ipAddress}</code></td>
                    <td>
                      {server.serverInfo ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(`${server.serverInfo.os} ${server.serverInfo.version} (${server.serverInfo.arch})\nKernel: ${server.serverInfo.kernel}\nMemory: ${server.serverInfo.memory}\nDisk: ${server.serverInfo.disk}`)}
                        >
                          <span style={{ cursor: 'help' }}>
                            {server.serverInfo.os} {server.serverInfo.version}
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <span className="text-muted">Unknown</span>
                      )}
                    </td>
                    <td>
                      <code>{server.sshUsername}@{server.sshPort}</code>
                      <Badge bg={server.authMethod === 'password' ? 'warning' : 'success'} className="ms-2" style={{ fontSize: '0.7rem' }}>
                        {server.authMethod === 'password' ? 'Password' : 'Key'}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={server.status === 'online' ? 'success' : 'danger'}>
                        <FontAwesomeIcon icon={server.status === 'online' ? faCheckCircle : faTimesCircle} className="me-1" />
                        {server.status}
                      </Badge>
                    </td>
                    <td>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDeleteServer(server.id)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Add Server Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#374151', borderColor: '#4b5563', color: '#fff' }}>
          <Modal.Title>
            <FontAwesomeIcon icon={faServer} className="me-2" />
            Add New Server
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Server Name <span className="text-danger">*</span>
                <OverlayTrigger placement="right" overlay={renderTooltip("Friendly name for this server (e.g., 'DigitalOcean US-East')")}>
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="e.g., DigitalOcean US-East"
                value={formData.name}
                onChange={handleInputChange}
                style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Hostname <span className="text-danger">*</span>
                <OverlayTrigger placement="right" overlay={renderTooltip("Fully qualified domain name (e.g., 'vps1.example.com')")}>
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                type="text"
                name="hostname"
                placeholder="e.g., vps1.example.com"
                value={formData.hostname}
                onChange={handleInputChange}
                style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                IP Address <span className="text-danger">*</span>
                <OverlayTrigger placement="right" overlay={renderTooltip("Public IP address of the server")}>
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                type="text"
                name="ipAddress"
                placeholder="e.g., 198.51.100.10"
                value={formData.ipAddress}
                onChange={handleInputChange}
                style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>SSH Port</Form.Label>
                  <Form.Control
                    type="number"
                    name="sshPort"
                    value={formData.sshPort}
                    onChange={handleInputChange}
                    style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>SSH Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="sshUsername"
                    value={formData.sshUsername}
                    onChange={handleInputChange}
                    style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>
                Authentication Method <span className="text-danger">*</span>
                <OverlayTrigger placement="right" overlay={renderTooltip("Use SSH key authentication for better security")}>
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
                </OverlayTrigger>
              </Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  label="Password"
                  name="authMethod"
                  value="password"
                  checked={formData.authMethod === 'password'}
                  onChange={handleInputChange}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Private Key (Recommended)"
                  name="authMethod"
                  value="privateKey"
                  checked={formData.authMethod === 'privateKey'}
                  onChange={handleInputChange}
                />
              </div>
            </Form.Group>

            {formData.authMethod === 'password' ? (
              <Form.Group className="mb-3">
                <Form.Label>SSH Password <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="password"
                  name="sshPassword"
                  placeholder="Enter SSH password"
                  value={formData.sshPassword}
                  onChange={handleInputChange}
                  style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
                />
                <Form.Text className="text-warning">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                  Password will be encrypted before storage
                </Form.Text>
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>SSH Private Key <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  name="sshPrivateKey"
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;...&#10;-----END OPENSSH PRIVATE KEY-----"
                  value={formData.sshPrivateKey}
                  onChange={handleInputChange}
                  style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563', fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                <Form.Text className="text-warning">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                  Private key will be encrypted before storage
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                Notes (Optional)
                <OverlayTrigger placement="right" overlay={renderTooltip("Add notes about this server (provider, region, purpose, etc.)")}>
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ fontSize: '0.8rem', cursor: 'help' }} />
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="notes"
                placeholder="e.g., DigitalOcean NYC3, 2GB RAM, Ubuntu 22.04"
                value={formData.notes}
                onChange={handleInputChange}
                style={{ backgroundColor: '#374151', color: '#e5e7eb', borderColor: '#4b5563' }}
              />
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center">
              <Button
                variant="info"
                onClick={handleTestConnection}
                disabled={testing || !formData.ipAddress || !formData.sshUsername || (!formData.sshPassword && !formData.sshPrivateKey)}
              >
                {testing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Testing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSync} className="me-2" />
                    Test Connection
                  </>
                )}
              </Button>

              {testResult && (
                <div className="flex-grow-1 ms-3">
                  {testResult.success ? (
                    <Alert variant="success" className="mb-0 py-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      ✅ Connected ({testResult.latency}ms) - {testResult.serverInfo?.os} {testResult.serverInfo?.version}
                    </Alert>
                  ) : (
                    <Alert variant="danger" className="mb-0 py-2">
                      <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                      ❌ {testResult.message}
                    </Alert>
                  )}
                </div>
              )}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#374151', borderColor: '#4b5563' }}>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddServer}
            disabled={!testResult?.success}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Server
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ServerRegistry;
